/* eslint-disable @typescript-eslint/camelcase */
import React, { ChangeEvent, FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  ISKU,
  fetchDataTrigger,
  resetAll,
  selectData,
  selectError,
  selectIsFetching,
  selectPage,
  selectPages,
  selectSort,
  updatePagination,
  updateSort,
} from "../../store/skuMapping";
import {
  fetchProductLinesRequest,
  selectProductLines,
  selectTenantCode,
} from "../../store/metadata";
import Can from "../../components/Can";
import { TableHeaderSort } from "../../components/Table/components/TableHeader/types";
import SKUMappingTable from "./components/SKUMappingTable";
import FilterSection from "./components/FilterSection";
import NewMappingModal from "./components/NewMappingModal";
import Pagination from "../../components/Pagination";
import ButtonPrimary from "../../components/ButtonPrimary";
import ExpandedRow from "./components/ExpandedRow";
import Message from "../../components/Table/components/Message";
import { ACTION, SUBJECT } from "../../constants/permissions";
import toasterService from "../../services/toaster";
import skuAPIService from "../../services/api/SKU";
import { ISKUPayload } from "../../services/api/SKU/types";
import { ContentLoaderContainer } from "../../components/Loader/ContentLoader/ContentLoaderContainer";
import { ITableDataParams } from "../../components/Table/types";

import "./styles.scss";

export const SKUMapping: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentPage = useSelector(selectPage);
  const pages = useSelector(selectPages);
  const defaultSort = useSelector(selectSort);
  const data = useSelector(selectData);
  const isFetching = useSelector(selectIsFetching);
  const error = useSelector(selectError);
  const productLines = useSelector(selectProductLines);
  const tenantCode = useSelector(selectTenantCode);

  const [skuMappingEntry, setSkuMappingEntry] = useState<ISKU>();
  const [showNewMappingDialog, setShowNewMappingDialog] = useState(false);
  const [showEditMappingDialog, setShowEditMappingDialog] = useState(false);
  const [isFetchingCreateSku, setIsFetchingCreateSku] = useState(false);
  const [isFetchingEditSku, setIsFetchingEditSku] = useState(false);

  useEffect(() => {
    dispatch(fetchProductLinesRequest());
    return () => {
      dispatch(resetAll());
    };
  }, [dispatch]);

  const handleSort = (sort: TableHeaderSort) => {
    dispatch(updateSort(sort));
  };

  const renderExpandedRow = (row: ITableDataParams<ISKU>["rowData"]) => {
    if (!row) {
      return <div />;
    }
    return (
      <ExpandedRow
        comment={row.comment}
        entitlements={row.product.entitlements}
      />
    );
  };

  const handlePageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const pageSize = parseInt(event.currentTarget.value);

    dispatch(updatePagination({ pageSize, page: 0 }));
  };

  const handlePageChange = (page: number) => {
    dispatch(updatePagination({ page }));
  };

  const toggleNewMapping = () => {
    return setShowNewMappingDialog(!showNewMappingDialog);
  };

  const toggleEditMapping = () => {
    return setShowEditMappingDialog((isVisible: boolean) => {
      return !isVisible;
    });
  };

  const handleEdit = (rowData: ITableDataParams<ISKU>["rowData"]) => {
    setSkuMappingEntry(rowData);
    toggleEditMapping();
  };

  const onEditDismiss = () => {
    toggleEditMapping();
    setSkuMappingEntry(undefined);
  };

  const createSKU = async (sku: ISKUPayload) => {
    try {
      setIsFetchingCreateSku(true);
      await skuAPIService.create(sku);
      dispatch(fetchDataTrigger());
      toasterService.success({
        content: t("createdSuccessfully", {
          sku: sku.sp_sku_code,
        }),
      });
      toggleNewMapping();
    } catch (err) {
      onError(err);
    } finally {
      setIsFetchingCreateSku(false);
    }
  };

  const editSKU = async ({
    product_code,
    comment,
    is_active,
    sp_sku_code,
    licenses,
  }: ISKUPayload) => {
    if (skuMappingEntry) {
      try {
        setIsFetchingEditSku(true);
        await skuAPIService.update(skuMappingEntry.id, {
          is_active,
          licenses,
          product_code,
          comment,
        });
        dispatch(fetchDataTrigger());
        toasterService.success({
          content: t("updatedSuccessfully", {
            sku: sp_sku_code,
          }),
        });

        onEditDismiss();
      } catch (err) {
        onError(err);
      } finally {
        setIsFetchingEditSku(false);
      }
    }
  };

  const onError = (err: any) => {
    if (err.response?.status === 409) {
      toasterService.error({ content: t("error.skuAlreadyExists") });
      return;
    }

    toasterService.error({ content: t("somethingWentWrong") });
  };

  return (
    <>
      <div className="title-block-wrapper">
        <h1>{t("sku_mapping")}</h1>
        <Can I={ACTION.WRITE} a={SUBJECT.PRODUCT}>
          <ButtonPrimary onClick={toggleNewMapping}>
            {t("newSKUMapping")}
          </ButtonPrimary>
        </Can>
      </div>
      <div className="mb-m">
        <FilterSection />
      </div>
      {isFetching ? (
        <ContentLoaderContainer />
      ) : error || !data.length ? (
        <Message>{t("nothingToDisplay")}</Message>
      ) : (
        <SKUMappingTable
          data={data}
          defaultSort={defaultSort}
          onSort={handleSort}
          onEdit={handleEdit}
          renderExpandedRow={renderExpandedRow}
        />
      )}
      <Pagination
        page={currentPage}
        pages={pages}
        onPageSizeChange={handlePageSizeChange}
        onPageChange={handlePageChange}
      />
      {showNewMappingDialog && (
        <Can I={ACTION.WRITE} a={SUBJECT.PRODUCT}>
          <NewMappingModal
            productLines={productLines}
            tenantCode={tenantCode}
            isOpen={showNewMappingDialog}
            isLoading={isFetchingCreateSku}
            onSubmit={createSKU}
            onDismiss={toggleNewMapping}
          />
        </Can>
      )}

      {showEditMappingDialog && (
        <Can I={ACTION.WRITE} a={SUBJECT.PRODUCT}>
          <NewMappingModal
            productLines={productLines}
            tenantCode={tenantCode}
            isLoading={isFetchingEditSku}
            isOpen={showEditMappingDialog}
            skuMapping={skuMappingEntry}
            onSubmit={editSKU}
            onDismiss={onEditDismiss}
          />
        </Can>
      )}
    </>
  );
};
